import { and, eq, gt } from '@tanstack/db'
import { useLiveQuery } from '@tanstack/react-db'
import { useMemo, useRef } from 'react'
import { getDayRange, pbDate } from '@/lib/dates'
import { useStore } from '@/lib/pocketbase'
import { combineWithOr } from '@/lib/query-utils'
import type { TimeEntries } from '@/types/pbSchema'

export interface CurrentEntrySummary {
    entryId: string
    clockIn: string
    entryType: string
    jobId: string | null
    jobName: string | null
    locationName: string | null
}

export interface BreakSummary {
    entryId: string
    clockIn: string
}

export interface DayEntrySummary {
    userId: string
    shiftStartAt: string // earliest non-break clock_in today
    currentEntry: CurrentEntrySummary | undefined
    onBreak?: BreakSummary
}

type TimeEntryStatus = TimeEntries['status']

// dayAnchor: any timestamp on the calendar day to query. The query selects
// entries whose clock_in is on/after the start of dayAnchor's day. Callers
// typically wire this to `useNowTick()` for live mode (so a page open across
// midnight recomputes the window) or to the snapshot timestamp when querying
// a different day.
//
// at: optional snapshot timestamp. When provided, currentEntry/onBreak reflect
// the entry that was open at `at` (clock_in <= at AND (clock_out IS NULL OR
// clock_out > at)) instead of the entry that is open right now. `at` must
// fall on the same calendar day as `dayAnchor`; otherwise the day-window
// query won't include it.
export function useTodayEntriesByUser(userIds: string[], dayAnchor: Date, at?: Date) {
    const [timeEntriesCollection, jobsCollection, locationsCollection] = useStore(
        'time_entries',
        'jobs',
        'locations'
    )

    const normalizedUserIds = useMemo(() => Array.from(new Set(userIds)).sort(), [userIds])

    // Stabilize dayStart's identity within a calendar day so the live query
    // below doesn't re-subscribe every nowTick. We compute it on every render
    // (cheap) but cache the previous value and return it when the day key
    // matches — passing a referentially-stable string into the dep array.
    const dayStart = useStableDayStart(dayAnchor)

    // Query all non-break entries for today. We need both the earliest
    // clock-in of the day (for the "Started" shift time) and the currently
    // active entry (for the site the user is on right now). Querying once
    // avoids running two live queries for the same data.
    const { data: rows = [], isLoading } = useLiveQuery(
        q => {
            if (normalizedUserIds.length === 0) return undefined
            return q
                .from({ entries: timeEntriesCollection })
                .join(
                    { job: jobsCollection },
                    ({ entries, job }) => eq(entries.job, job.id),
                    'left'
                )
                .join(
                    { location: locationsCollection },
                    ({ entries, location }) => eq(entries.location, location.id),
                    'left'
                )
                .where(({ entries }) =>
                    and(
                        combineWithOr(normalizedUserIds.map(id => eq(entries.user, id))),
                        gt(entries.clock_in, dayStart)
                    )
                )
                .orderBy(({ entries }) => entries.clock_in, 'asc')
        },
        [normalizedUserIds, dayStart]
    )

    // Snapshot mode picks the entry that was open at `at` rather than the
    // entry that is open right now (status === 'active').
    const atIso = at ? at.toISOString() : null
    const isOpenAt = useMemo(() => makeIsOpenAt(atIso), [atIso])

    const byUserId = useMemo(() => buildByUserId(rows, isOpenAt), [rows, isOpenAt])

    return { dayEntries: byUserId, isPending: isLoading }
}

function useStableDayStart(dayAnchor: Date): string {
    const dayKey = dayAnchor.toDateString()
    const cache = useRef<{ key: string; value: string } | null>(null)
    if (!cache.current || cache.current.key !== dayKey) {
        cache.current = { key: dayKey, value: pbDate(getDayRange(dayAnchor).startDate) }
    }
    return cache.current.value
}

/** @internal Exposed only for unit tests of buildByUserId. */
export type EntryRow = {
    entries: {
        id: string
        user: string
        clock_in: string
        clock_out?: string | null
        status?: TimeEntryStatus
        job?: string
        entry_type?: string
    }
    job?: { id: string; name?: string } | null
    location?: { name?: string } | null
}

type EntryOpenInput = {
    clock_in: string
    clock_out?: string | null
    status?: TimeEntryStatus
}

export type IsOpenAt = (entry: EntryOpenInput) => boolean

// Returns a predicate that decides whether an entry was open at the given
// snapshot time. When atIso is null, falls back to entry.status === 'active'
// (the live behavior). An entry is open at T when clock_in <= T AND
// (clock_out is null OR clock_out > T).
export function makeIsOpenAt(atIso: string | null): IsOpenAt {
    return entry => {
        if (!atIso) return entry.status === 'active'
        if (entry.clock_in > atIso) return false
        if (entry.clock_out && entry.clock_out <= atIso) return false
        return true
    }
}

function buildCurrentEntry(row: EntryRow): CurrentEntrySummary {
    const entry = row.entries
    return {
        entryId: entry.id,
        clockIn: entry.clock_in,
        entryType: entry.entry_type ?? 'manual',
        jobId: row.job?.id ?? entry.job ?? null,
        jobName: row.job?.name ?? null,
        locationName: row.location?.name ?? null,
    }
}

export function buildByUserId(rows: unknown[], isOpenAt: IsOpenAt): Map<string, DayEntrySummary> {
    const map = new Map<string, DayEntrySummary>()

    // Rows are sorted ascending by clock_in. The first non-break row we see
    // for a user sets shiftStartAt; breaks don't count toward shift start,
    // but we still track the currently open one (if any) so the card can
    // surface it. There should be at most one open break per user at a time.
    for (const row of rows as EntryRow[]) {
        const entry = row.entries
        if (!entry?.user) continue

        if (entry.entry_type === 'break') {
            applyBreakRow(map, row, isOpenAt)
        } else {
            applyJobRow(map, row, isOpenAt)
        }
    }
    return map
}

function applyBreakRow(map: Map<string, DayEntrySummary>, row: EntryRow, isOpenAt: IsOpenAt): void {
    const entry = row.entries
    if (!isOpenAt(entry)) return
    const existing = map.get(entry.user)
    const summary = existing ?? {
        userId: entry.user,
        shiftStartAt: entry.clock_in,
        currentEntry: undefined,
        onBreak: undefined,
    }
    summary.onBreak = { entryId: entry.id, clockIn: entry.clock_in }
    if (!existing) map.set(entry.user, summary)
}

function applyJobRow(map: Map<string, DayEntrySummary>, row: EntryRow, isOpenAt: IsOpenAt): void {
    const entry = row.entries
    const open = isOpenAt(entry)
    const existing = map.get(entry.user)
    if (!existing) {
        map.set(entry.user, {
            userId: entry.user,
            shiftStartAt: entry.clock_in,
            currentEntry: open ? buildCurrentEntry(row) : undefined,
            onBreak: undefined,
        })
    } else if (open) {
        existing.currentEntry = buildCurrentEntry(row)
    }
}
